/**
 * "Absender für mich privat markieren" in the Bearbeitungsmaske (P2 hardening 04), replacing the
 * old hard "Absender für mich blockieren". Per-user, own view only: hides the sender from the
 * caller's Posteingang while the mail stays reachable in /kommunikation + search. Only for an
 * e-mail sender with a single address.
 *
 * STRICTLY separate from the per-mail Privat-Markierung (communication.is_private). The copy says
 * "Gilt nur für Sie" so a per-user private is never confused with the firm-wide block.
 */

import { useState } from 'react';
import { Ban, Check, EyeOff } from 'lucide-react';
import { extractEmailAddress } from '@brokly/shared';
import { Button } from '../../components/ui/Button';
import { useEffectivePermissions } from '../administration/useEffectivePermissions';
import { useSenderPrivateList, useSenderPrivatePreview } from './useSenderPrivate';
import { SenderPrivateMarkDialog, SenderPrivateUnmarkDialog } from './SenderPrivateDialog';

export function SenderPrivateButton({ from, channel }: { from: string | null; channel: string | null }) {
  const address = channel === 'email' || channel === null ? extractEmailAddress(from) : null;
  const { can } = useEffectivePermissions();
  const { data: mine = [] } = useSenderPrivateList();
  const [open, setOpen] = useState<null | 'mark' | 'unmark'>(null);

  // Always preview so we know firm_blocked + the current private state; the mark dialog reuses it.
  const preview = useSenderPrivatePreview(address, Boolean(address));

  if (!address) return null;

  const openRow = mine.find((r) => r.address === address && r.open) ?? null;
  const isPrivate = openRow != null || preview.data?.is_private === true;
  const firmBlocked = preview.data?.firm_blocked === true;

  // State 3: firm-blocked → nothing to hide for new mail; admins manage past via the admin page.
  if (firmBlocked && !isPrivate) {
    return (
      <p className="mt-3 flex items-center gap-1.5 text-xs text-muted-foreground" role="status">
        <Check className="size-3.5" />
        {address} ist firmenweit blockiert (Admin-Regel).
      </p>
    );
  }

  // State 2: private for me (open row) → status + "Nicht mehr privat".
  if (isPrivate) {
    if (open === 'unmark' && (openRow?.id || preview.data?.open_row_id)) {
      return (
        <SenderPrivateUnmarkDialog
          address={address}
          rowId={(openRow?.id ?? preview.data?.open_row_id)!}
          onClose={() => setOpen(null)}
        />
      );
    }
    return (
      <p className="mt-3 flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground" role="status">
        <Check className="size-3.5" />
        {address} ist für Sie privat markiert. Neue E-Mails erscheinen nicht in Ihrem Posteingang.
        <button
          type="button"
          className="underline underline-offset-2 hover:text-foreground"
          onClick={() => setOpen('unmark')}
        >
          Nicht mehr privat
        </button>
      </p>
    );
  }

  // State 1: not private, not firm-blocked.
  if (open === 'mark') {
    return (
      <SenderPrivateMarkDialog
        address={address}
        preview={preview.data}
        previewLoading={preview.isLoading}
        onClose={() => setOpen(null)}
      />
    );
  }

  return (
    <div className="mt-3 flex flex-col gap-1">
      <div className="flex items-center gap-2">
        <Button size="sm" variant="outline" onClick={() => setOpen('mark')}>
          <EyeOff className="size-3.5" /> Absender für mich privat markieren
        </Button>
        {can('tenant.manage') && (
          <>
            <span className="h-5 w-px bg-border" aria-hidden />
            <Button
              size="sm"
              variant="ghost"
              title="Für alle Mitarbeitenden. Neue E-Mails werden nicht mehr synchronisiert."
              onClick={() => {
                // The firm-wide block is managed on the admin page (Administration › E-Mail-Blockliste).
                window.location.assign('/administration/email-blocklist');
              }}
            >
              <Ban className="size-3.5" /> Firmenweit blockieren
            </Button>
          </>
        )}
      </div>
      {can('tenant.manage') && (
        <p className="text-xs text-muted-foreground">
          Privat = nur für Sie, E-Mail bleibt im Postfach. Blockieren = für die ganze Firma, Admin.
        </p>
      )}
    </div>
  );
}
